import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WelcomeQuestionsListTab from './WelcomeQuestionsListTab';
import UserQuestionAnswersSubTab from './UserQuestionAnswersSubTab';
import DatasetsSubTab from './DatasetsSubTab';
import './QuestionsTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function QuestionsTab() {
  const [activeTab, setActiveTab] = useState('generate'); // 'generate', 'list', 'welcome', 'answers', 'statistics', 'datasets'
  
  return (
    <div className="questions-tab">
      <div className="tab-header">
        <h2>Управление вопросами</h2>
      </div>
      
      <div className="sub-tabs">
        <button
          className={`sub-tab ${activeTab === 'generate' ? 'active' : ''}`}
          onClick={() => setActiveTab('generate')}
        >
          Генерация вопросов из текста
        </button>
        <button
          className={`sub-tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Список вопросов
        </button>
        <button
          className={`sub-tab ${activeTab === 'welcome' ? 'active' : ''}`}
          onClick={() => setActiveTab('welcome')}
        >
          Welcome-вопросы
        </button>
        <button
          className={`sub-tab ${activeTab === 'answers' ? 'active' : ''}`}
          onClick={() => setActiveTab('answers')}
        >
          Ответы на вопросы
        </button>
        <button
          className={`sub-tab ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          Статистика
        </button>
        <button
          className={`sub-tab ${activeTab === 'datasets' ? 'active' : ''}`}
          onClick={() => setActiveTab('datasets')}
        >
          Датасеты
        </button>
      </div>
      
      {activeTab === 'generate' && <QuestionGenerationTab />}
      {activeTab === 'list' && <QuestionsListTab />}
      {activeTab === 'welcome' && <WelcomeQuestionsListTab />}
      {activeTab === 'answers' && <UserQuestionAnswersSubTab />}
      {activeTab === 'statistics' && <QuestionsStatisticsTab />}
      {activeTab === 'datasets' && <DatasetsSubTab />}
    </div>
  );
}

// Компонент для генерации вопросов
const TARGET_MAIN = 'main';
const TARGET_WELCOME = 'welcome';

// Элемент этапа 1: только текст вопроса. Этап 2: полный объект с question, correct_answer, grade, technology.
function isEnrichedItem(item) {
  return item && typeof item.correct_answer === 'string';
}

/**
 * Парсит ответ API со списком вопросов. Вопросы могут приходить массивом строк;
 * каждая строка может содержать несколько вопросов, разделённых точкой с запятой (;).
 * Как во вкладке «Генерация вопросов из текста» в Сертификациях.
 */
function parseQuestionsFromResponse(data) {
  if (data == null) return [];
  const result = [];
  if (Array.isArray(data)) {
    for (const item of data) {
      if (typeof item === 'string') {
        const trimmed = item.replace(/^[\s\[\]\n"]+|[\s\[\]\n"]+$/g, '').trim();
        if (trimmed.includes(';')) {
          const split = trimmed.split(/\s*;\s*/).map((s) => s.trim()).filter(Boolean);
          result.push(...split.map((text) => ({ text })));
        } else if (trimmed) {
          result.push({ text: trimmed });
        }
      }
    }
  }
  return result;
}

function QuestionGenerationTab() {
  const [targetTable, setTargetTable] = useState(TARGET_MAIN); // 'main' | 'welcome'
  const [text, setText] = useState('');
  const [generatedItems, setGeneratedItems] = useState([]); // [{ text }] или [{ question, correct_answer, grade, technology }]
  const [loading, setLoading] = useState(false);
  const [addingInfo, setAddingInfo] = useState(false); // прогресс «Добавить информацию»
  const [error, setError] = useState(null);
  const [topics, setTopics] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/topics`);
      setTopics(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке тем:', err);
    }
  };

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Введите текст для генерации вопросов');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/generate`, { text: text.trim() });
      const raw = response.data?.questions ?? [];
      const items = parseQuestionsFromResponse(raw);
      setGeneratedItems(items.length > 0 ? items : [{ text: '' }]);
    } catch (err) {
      setError('Ошибка при генерации вопросов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddInfo = async () => {
    const toEnrich = generatedItems.filter((item) => !isEnrichedItem(item) && item.text && item.text.trim());
    if (toEnrich.length === 0) {
      setError('Нет вопросов для обогащения или добавьте текст вопроса');
      return;
    }
    setAddingInfo(true);
    setError(null);
    const updated = [...generatedItems];
    let idx = 0;
    for (let i = 0; i < updated.length; i++) {
      if (!isEnrichedItem(updated[i]) && updated[i].text && updated[i].text.trim()) {
        try {
          const res = await axios.post(`${API_URL}/api/questions/question-prepare`, {
            question_text: updated[i].text.trim(),
          });
          const d = res.data;
          let technology = (d.technology || '').trim();
          const exactMatch = topics.find((t) => t.title.toLowerCase() === technology.toLowerCase());
          const partialMatch = exactMatch ? null : topics.find((t) => t.title.toLowerCase().includes(technology.toLowerCase()) || technology.toLowerCase().includes(t.title.toLowerCase()));
          technology = exactMatch ? exactMatch.title : partialMatch ? partialMatch.title : technology;
          updated[i] = {
            question: (d.question || '').trim(),
            correct_answer: (d.correct_answer || '').trim(),
            grade: (d.grade || 'junior').toLowerCase(),
            technology,
          };
        } catch (err) {
          setError(`Ошибка при обогащении вопроса ${idx + 1}: ${err.response?.data?.detail || err.message}`);
          setAddingInfo(false);
          return;
        }
        idx += 1;
      }
    }
    setGeneratedItems(updated);
    setAddingInfo(false);
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...generatedItems];
    updated[index] = { ...updated[index], [field]: value };
    setGeneratedItems(updated);
  };

  const handleDeleteQuestion = (index) => {
    setGeneratedItems(generatedItems.filter((_, i) => i !== index));
  };

  const handleAddQuestion = () => {
    if (generatedItems.length > 0 && generatedItems.every(isEnrichedItem)) {
      setGeneratedItems([
        ...generatedItems,
        { question: '', correct_answer: '', grade: 'junior', technology: '' },
      ]);
    } else {
      setGeneratedItems([...generatedItems, { text: '' }]);
    }
  };

  const handleSave = async () => {
    const enriched = generatedItems.filter(isEnrichedItem);
    if (enriched.length === 0) {
      setError('Нет вопросов для сохранения. Нажмите «Добавить информацию» и дождитесь завершения.');
      return;
    }
    const invalidQuestions = enriched.filter(
      (q) => !q.question.trim() || !q.correct_answer.trim() || !q.technology.trim() || !topics.find((t) => t.title === q.technology)
    );
    if (invalidQuestions.length > 0) {
      setError('Заполните все поля для всех вопросов и убедитесь, что выбрана существующая тема');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (targetTable === TARGET_WELCOME) {
        let saved = 0;
        let skipped = 0;
        for (const q of enriched) {
          const topic = topics.find((t) => t.title === q.technology);
          if (!topic) {
            skipped += 1;
            continue;
          }
          const category = q.grade === 'junior' ? 'Junior' : q.grade === 'middle' ? 'Middle' : 'Senior';
          await axios.post(`${API_URL}/api/welcome-questions`, {
            topic_id: topic.id,
            text: q.question.trim(),
            category,
            correct_answer: q.correct_answer.trim() || null,
          });
          saved += 1;
        }
        alert(`Сохранено в Welcome-вопросы: ${saved}. Пропущено (темы не найдены): ${skipped}`);
      } else {
        const response = await axios.post(`${API_URL}/api/questions/save-generated`, { questions: enriched });
        alert(`Сохранено вопросов: ${response.data.saved_count}. Пропущено (темы не найдены): ${response.data.skipped_count}`);
      }
      setText('');
      setGeneratedItems([]);
    } catch (err) {
      setError('Ошибка при сохранении вопросов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getTopicTitle = (technology) => {
    const topic = topics.find((t) => t.title.toLowerCase() === technology.toLowerCase());
    return topic ? topic.title : technology;
  };

  const hasPhase1Items = generatedItems.length > 0 && generatedItems.some((item) => !isEnrichedItem(item));
  const allEnriched = generatedItems.length > 0 && generatedItems.every(isEnrichedItem);

  if (generatedItems.length === 0) {
    return (
      <div className="generation-container">
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-container">
          <h3>Генерация вопросов из текста</h3>
          <div className="form-group">
            <label htmlFor="target-table">Куда сохранять вопросы *</label>
            <select
              id="target-table"
              value={targetTable}
              onChange={(e) => setTargetTable(e.target.value)}
              disabled={loading}
            >
              <option value={TARGET_MAIN}>Основная база вопросов</option>
              <option value={TARGET_WELCOME}>Welcome-вопросы</option>
            </select>
            <small className="form-hint">
              {targetTable === TARGET_WELCOME
                ? 'Вопросы будут добавлены в конец списка Welcome-вопросов с автоматической нумерацией.'
                : 'Вопросы будут сохранены в основную базу (таблица questions).'}
            </small>
          </div>
          <div className="form-group">
            <label htmlFor="text">Введите текст для анализа *</label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={12}
              placeholder="Вставьте текст, из которого нужно извлечь вопросы..."
              disabled={loading}
            />
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={loading || !text.trim()}
            >
              {loading ? 'Генерация...' : 'Сгенерировать вопросы'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="generation-container">
      {error && <div className="error-message">{error}</div>}

      <div className="form-container" style={{ marginBottom: '1rem' }}>
        <div className="form-group">
          <label htmlFor="target-table">Куда сохранять вопросы *</label>
          <select
            id="target-table"
            value={targetTable}
            onChange={(e) => setTargetTable(e.target.value)}
            disabled={loading || addingInfo}
          >
            <option value={TARGET_MAIN}>Основная база вопросов</option>
            <option value={TARGET_WELCOME}>Welcome-вопросы</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="text">Введите текст для анализа *</label>
          <textarea
            id="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder="Вставьте текст, из которого нужно извлечь вопросы..."
            disabled={loading || addingInfo}
          />
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={loading || addingInfo || !text.trim()}
          >
            {loading ? 'Генерация...' : 'Сгенерировать вопросы'}
          </button>
        </div>
      </div>

      {hasPhase1Items && (
        <>
          <div className="generated-questions-header">
            <h3>Сгенерированные вопросы ({generatedItems.length})</h3>
            <div className="header-actions">
              <button type="button" className="btn btn-secondary" onClick={handleAddQuestion}>
                + Добавить вопрос
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddInfo}
                disabled={addingInfo || !generatedItems.some((item) => item.text && item.text.trim())}
              >
                {addingInfo ? 'Добавление информации...' : 'Добавить информацию'}
              </button>
            </div>
          </div>
          <div className="generated-questions-list">
            {generatedItems.map((item, index) => (
              <div key={index} className="generated-question-card">
                <div className="question-card-header">
                  <div className="question-card-header-left">
                    <span className="question-number">Вопрос {index + 1}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-icon btn-delete"
                    onClick={() => handleDeleteQuestion(index)}
                    title="Удалить вопрос"
                  >
                    ✕
                  </button>
                </div>
                <div className="form-group full-width" style={{ margin: 0 }}>
                  <textarea
                    value={item.text ?? ''}
                    onChange={(e) => handleQuestionChange(index, 'text', e.target.value)}
                    rows={2}
                    placeholder="Текст вопроса"
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {allEnriched && (
        <>
          <div className="generated-questions-header">
            <h3>Сгенерированные вопросы ({generatedItems.length})</h3>
            <div className="header-actions">
              <span className="save-target-hint">
                Сохранение: {targetTable === TARGET_WELCOME ? 'Welcome-вопросы' : 'Основная база вопросов'}
              </span>
              <button type="button" className="btn btn-secondary" onClick={handleAddQuestion}>
                + Добавить вопрос
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || topics.length === 0}
                title={topics.length === 0 ? 'Сначала создайте тему во вкладке "Темы"' : ''}
              >
                {saving ? 'Сохранение...' : 'Сохранить все вопросы'}
              </button>
            </div>
          </div>
          <div className="generated-questions-list">
            {generatedItems.map((q, index) => (
              <div key={index} className="generated-question-card">
                <div className="question-card-header">
                  <div className="question-card-header-left">
                    <span className="question-number">Вопрос {index + 1}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-icon btn-delete"
                    onClick={() => handleDeleteQuestion(index)}
                    title="Удалить вопрос"
                  >
                    ✕
                  </button>
                </div>
                <div className="question-card-body">
                  <div className="form-group">
                    <label>Технология (Тема) *</label>
                    <select
                      value={q.technology}
                      onChange={(e) => handleQuestionChange(index, 'technology', e.target.value)}
                      required
                      disabled={topics.length === 0}
                    >
                      <option value="">{topics.length === 0 ? 'Нет доступных тем' : 'Выберите тему'}</option>
                      {topics.map((topic) => (
                        <option key={topic.id} value={topic.title}>
                          {topic.title}
                        </option>
                      ))}
                    </select>
                    {topics.length === 0 ? (
                      <small className="form-hint warning">⚠ Сначала создайте тему во вкладке "Темы"</small>
                    ) : q.technology && topics.find((t) => t.title === q.technology) ? (
                      <small className="form-hint success">✓ Тема выбрана</small>
                    ) : q.technology ? (
                      <small className="form-hint warning">⚠ Выберите тему из списка</small>
                    ) : (
                      <small className="form-hint">Выберите тему из списка</small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Грейд *</label>
                    <select
                      value={q.grade}
                      onChange={(e) => handleQuestionChange(index, 'grade', e.target.value)}
                    >
                      <option value="junior">Junior</option>
                      <option value="middle">Middle</option>
                      <option value="senior">Senior</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Текст вопроса *</label>
                    <textarea
                      value={q.question}
                      onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                      rows={3}
                      placeholder="Текст вопроса"
                    />
                  </div>
                  <div className="form-group">
                    <label>Правильный ответ *</label>
                    <textarea
                      value={q.correct_answer}
                      onChange={(e) => handleQuestionChange(index, 'correct_answer', e.target.value)}
                      rows={4}
                      placeholder="Правильный ответ на вопрос"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Компонент для списка вопросов с пагинацией и фильтрацией
function QuestionsListTab() {
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [formData, setFormData] = useState({ topic_id: '', text: '', category: '', correct_answer: '' });
  const [regeneratingAnswer, setRegeneratingAnswer] = useState(false);

  // Форма «Сделать Welcome»: вопрос для переноса и данные формы
  const [makeWelcomeQuestion, setMakeWelcomeQuestion] = useState(null);
  const [makeWelcomeFormData, setMakeWelcomeFormData] = useState({ topic_id: '', text: '', category: '', correct_answer: '' });
  const [makeWelcomeRegenerating, setMakeWelcomeRegenerating] = useState(false);
  const [makeWelcomeSaving, setMakeWelcomeSaving] = useState(false);

  // ID вопроса, для которого идёт пересоздание ответа в списке (без открытия формы)
  const [regeneratingAnswerId, setRegeneratingAnswerId] = useState(null);
  
  // Фильтры и пагинация
  const [filters, setFilters] = useState({
    topic_id: '',
    category: '',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, filters]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/topics`);
      setTopics(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке тем:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const params = {
        skip,
        limit: pageSize
      };
      
      if (filters.topic_id) {
        params.topic_id = parseInt(filters.topic_id);
      }
      if (filters.category) {
        params.category = filters.category;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      
      const [questionsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/questions`, { params }),
        axios.get(`${API_URL}/api/questions/count`, { params: { ...params, skip: undefined, limit: undefined } })
      ]);
      
      setQuestions(questionsRes.data);
      setTotalCount(countRes.data.count);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        topic_id: parseInt(formData.topic_id),
        text: formData.text,
        category: formData.category || null,
        correct_answer: formData.correct_answer || null
      };
      
      if (editingQuestionId) {
        await axios.put(`${API_URL}/api/questions/${editingQuestionId}`, payload);
        setEditingQuestionId(null);
      } else {
        await axios.post(`${API_URL}/api/questions`, payload);
        setShowForm(false);
      }
      setRegeneratingAnswer(false);
      setFormData({ topic_id: '', text: '', category: '', correct_answer: '' });
      fetchData();
    } catch (err) {
      setError('Ошибка при сохранении вопроса: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (question) => {
    setEditingQuestionId(question.id);
    setFormData({
      topic_id: question.topic_id.toString(),
      text: question.text,
      category: question.category || '',
      correct_answer: question.correct_answer || ''
    });
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот вопрос?')) {
      try {
        await axios.delete(`${API_URL}/api/questions/${id}`);
        fetchData();
      } catch (err) {
        setError('Ошибка при удалении вопроса: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  /** Пересоздать ответ по нейросети для вопроса в списке, сохранить в БД и обновить отображение без перезагрузки. */
  const handleRegenerateAnswerInList = async (question) => {
    const questionText = (question.text || '').trim();
    if (!questionText) {
      setError('У вопроса отсутствует текст для пересоздания ответа');
      return;
    }
    try {
      setRegeneratingAnswerId(question.id);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText,
      });
      const newCorrectAnswer = response.data.correct_answer || '';
      await axios.put(`${API_URL}/api/questions/${question.id}`, {
        topic_id: question.topic_id,
        text: question.text,
        category: question.category || null,
        correct_answer: newCorrectAnswer || null,
      });
      setQuestions((prev) =>
        prev.map((q) => (q.id === question.id ? { ...q, correct_answer: newCorrectAnswer } : q))
      );
    } catch (err) {
      setError('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegeneratingAnswerId(null);
    }
  };

  const handleRegenerateAnswer = async () => {
    const questionText = (formData.text || '').trim();
    if (!questionText) {
      setError('Введите текст вопроса для пересоздания ответа');
      return;
    }
    try {
      setRegeneratingAnswer(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText
      });
      setFormData(prev => ({ ...prev, correct_answer: response.data.correct_answer || '' }));
    } catch (err) {
      setError('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegeneratingAnswer(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingQuestionId(null);
    setRegeneratingAnswer(false);
    setFormData({ topic_id: '', text: '', category: '', correct_answer: '' });
  };

  const handleOpenMakeWelcome = (question) => {
    setMakeWelcomeQuestion(question);
    setMakeWelcomeFormData({
      topic_id: question.topic_id.toString(),
      text: question.text || '',
      category: question.category || '',
      correct_answer: question.correct_answer || '',
    });
    setError(null);
  };

  const handleCloseMakeWelcome = () => {
    setMakeWelcomeQuestion(null);
    setMakeWelcomeFormData({ topic_id: '', text: '', category: '', correct_answer: '' });
    setMakeWelcomeRegenerating(false);
    setMakeWelcomeSaving(false);
  };

  const handleMakeWelcomeRegenerateAnswer = async () => {
    const questionText = (makeWelcomeFormData.text || '').trim();
    if (!questionText) {
      setError('Введите текст вопроса для пересоздания ответа');
      return;
    }
    try {
      setMakeWelcomeRegenerating(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText,
      });
      setMakeWelcomeFormData((prev) => ({ ...prev, correct_answer: response.data.correct_answer || '' }));
    } catch (err) {
      setError('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMakeWelcomeRegenerating(false);
    }
  };

  const handleMakeWelcomeSubmit = async (e) => {
    e.preventDefault();
    if (!makeWelcomeQuestion) return;
    try {
      setMakeWelcomeSaving(true);
      setError(null);
      await axios.post(`${API_URL}/api/welcome-questions`, {
        topic_id: parseInt(makeWelcomeFormData.topic_id, 10),
        text: makeWelcomeFormData.text.trim(),
        category: makeWelcomeFormData.category || null,
        correct_answer: makeWelcomeFormData.correct_answer.trim() || null,
      });
      await axios.delete(`${API_URL}/api/questions/${makeWelcomeQuestion.id}`);
      handleCloseMakeWelcome();
      fetchData();
    } catch (err) {
      setError('Ошибка при переносе в Welcome: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMakeWelcomeSaving(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  };

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ topic_id: '', category: '', search: '' });
    setCurrentPage(1);
  };

  const getTopicTitle = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.title : `ID: ${topicId}`;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && questions.length === 0) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="questions-list-tab">
      <div className="tab-header">
        <h3>Список вопросов</h3>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить вопрос
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Фильтры и поиск */}
      <div className="filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="filter-topic">Тема:</label>
            <select
              id="filter-topic"
              value={filters.topic_id}
              onChange={(e) => handleFilterChange('topic_id', e.target.value)}
              className="filter-select"
            >
              <option value="">Все темы</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.title}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="filter-category">Категория:</label>
            <select
              id="filter-category"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="filter-select"
            >
              <option value="">Все категории</option>
              <option value="Junior">Junior</option>
              <option value="Middle">Middle</option>
              <option value="Senior">Senior</option>
            </select>
          </div>
          
          <div className="filter-group filter-search">
            <label htmlFor="filter-search">Поиск:</label>
            <input
              id="filter-search"
              type="text"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Поиск по тексту вопроса..."
              className="filter-input"
            />
          </div>
          
          <button 
            className="btn btn-secondary btn-clear-filters"
            onClick={clearFilters}
            disabled={!filters.topic_id && !filters.category && !filters.search}
          >
            Сбросить фильтры
          </button>
        </div>
        
        <div className="results-info">
          Найдено вопросов: <strong>{totalCount}</strong>
        </div>
      </div>

      {makeWelcomeQuestion && (
        <div className="form-container make-welcome-form">
          <h3>Перенос вопроса в Welcome-вопросы</h3>
          <p className="form-hint make-welcome-hint">
            Вопрос будет добавлен в таблицу Welcome-вопросов и удалён из основной базы. Заполните поля при необходимости и нажмите «Сделать Welcome вопросом».
          </p>
          <form onSubmit={handleMakeWelcomeSubmit}>
            <div className="form-group">
              <label htmlFor="make-welcome-topic_id">Тема *</label>
              <select
                id="make-welcome-topic_id"
                value={makeWelcomeFormData.topic_id}
                onChange={(e) => setMakeWelcomeFormData({ ...makeWelcomeFormData, topic_id: e.target.value })}
                required
                disabled={topics.length === 0}
              >
                <option value="">Выберите тему</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="make-welcome-category">Категория</label>
              <select
                id="make-welcome-category"
                value={makeWelcomeFormData.category}
                onChange={(e) => setMakeWelcomeFormData({ ...makeWelcomeFormData, category: e.target.value })}
              >
                <option value="">Выберите категорию</option>
                <option value="Junior">Junior</option>
                <option value="Middle">Middle</option>
                <option value="Senior">Senior</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="make-welcome-text">Текст вопроса *</label>
              <textarea
                id="make-welcome-text"
                value={makeWelcomeFormData.text}
                onChange={(e) => setMakeWelcomeFormData({ ...makeWelcomeFormData, text: e.target.value })}
                required
                rows={4}
                placeholder="Текст вопроса"
              />
            </div>
            <div className="form-group">
              <div className="form-group-label-row">
                <label htmlFor="make-welcome-correct_answer">Правильный ответ</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-regenerate-answer"
                  onClick={handleMakeWelcomeRegenerateAnswer}
                  disabled={makeWelcomeRegenerating || !(makeWelcomeFormData.text || '').trim()}
                  title={!(makeWelcomeFormData.text || '').trim() ? 'Сначала введите текст вопроса' : 'Пересоздать эталонный ответ нейросетью'}
                >
                  {makeWelcomeRegenerating ? '⏳ Пересоздание...' : '🔄 Пересоздать ответ'}
                </button>
              </div>
              <textarea
                id="make-welcome-correct_answer"
                value={makeWelcomeFormData.correct_answer}
                onChange={(e) => setMakeWelcomeFormData({ ...makeWelcomeFormData, correct_answer: e.target.value })}
                rows={4}
                placeholder="Правильный ответ (необязательно)"
                disabled={makeWelcomeRegenerating}
              />
            </div>
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={makeWelcomeSaving || topics.length === 0}
              >
                {makeWelcomeSaving ? 'Перенос...' : 'Сделать Welcome вопросом'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseMakeWelcome}
                disabled={makeWelcomeSaving}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {showForm && !editingQuestionId && (
        <div className="form-container">
          <h3>Добавить новый вопрос</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="topic_id">Тема вопроса *</label>
              <select
                id="topic_id"
                value={formData.topic_id}
                onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
                required
                disabled={topics.length === 0}
              >
                <option value="">Выберите тему</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.title}
                  </option>
                ))}
              </select>
              {topics.length === 0 && (
                <small className="form-hint">
                  Сначала создайте тему во вкладке "Темы"
                </small>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="text">Текст вопроса *</label>
              <textarea
                id="text"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                required
                rows={4}
                placeholder="Введите текст вопроса"
              />
            </div>
            <div className="form-group">
              <label htmlFor="category">Категория *</label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <option value="">Выберите категорию</option>
                <option value="Junior">Junior</option>
                <option value="Middle">Middle</option>
                <option value="Senior">Senior</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="correct_answer">Правильный ответ</label>
              <textarea
                id="correct_answer"
                value={formData.correct_answer}
                onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                rows={4}
                placeholder="Введите правильный ответ на вопрос (необязательно)"
                disabled={regeneratingAnswer}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={topics.length === 0}>
                Создать
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Тема</th>
              <th>Категория</th>
              <th>Текст вопроса</th>
              <th>Правильный ответ</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  Загрузка...
                </td>
              </tr>
            ) : questions.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  {filters.topic_id || filters.category || filters.search 
                    ? 'Вопросы не найдены по заданным фильтрам'
                    : 'Нет вопросов. Добавьте первый вопрос.'}
                </td>
              </tr>
            ) : (
              questions.map((question) => (
                <React.Fragment key={question.id}>
                  {editingQuestionId === question.id ? (
                    <tr className="questions-list-inline-edit-row">
                      <td colSpan="6">
                        <div className="questions-list-inline-edit-form">
                          <h4>Редактировать вопрос</h4>
                          <form onSubmit={handleSubmit}>
                            <div className="form-group">
                              <label htmlFor={`inline-topic_id-${question.id}`}>Тема вопроса *</label>
                              <select
                                id={`inline-topic_id-${question.id}`}
                                value={formData.topic_id}
                                onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
                                required
                                disabled={topics.length === 0}
                              >
                                <option value="">Выберите тему</option>
                                {topics.map((topic) => (
                                  <option key={topic.id} value={topic.id}>
                                    {topic.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label htmlFor={`inline-text-${question.id}`}>Текст вопроса *</label>
                              <textarea
                                id={`inline-text-${question.id}`}
                                value={formData.text}
                                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                                required
                                rows={4}
                                placeholder="Введите текст вопроса"
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor={`inline-category-${question.id}`}>Категория *</label>
                              <select
                                id={`inline-category-${question.id}`}
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                required
                              >
                                <option value="">Выберите категорию</option>
                                <option value="Junior">Junior</option>
                                <option value="Middle">Middle</option>
                                <option value="Senior">Senior</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <div className="form-group-label-row">
                                <label htmlFor={`inline-correct_answer-${question.id}`}>Правильный ответ</label>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-regenerate-answer"
                                  onClick={handleRegenerateAnswer}
                                  disabled={regeneratingAnswer || !(formData.text || '').trim()}
                                  title={!(formData.text || '').trim() ? 'Сначала введите текст вопроса' : 'Пересоздать эталонный ответ нейросетью'}
                                >
                                  {regeneratingAnswer ? '⏳ Пересоздание...' : '🔄 Пересоздать ответ'}
                                </button>
                              </div>
                              <textarea
                                id={`inline-correct_answer-${question.id}`}
                                value={formData.correct_answer}
                                onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                                rows={4}
                                placeholder="Введите правильный ответ на вопрос (необязательно)"
                                disabled={regeneratingAnswer}
                              />
                              <small className="form-hint">
                                Кнопка «Пересоздать ответ» отправит текст вопроса в нейросеть и подставит эталонный ответ в поле выше.
                              </small>
                            </div>
                            <div className="form-actions">
                              <button type="submit" className="btn btn-primary" disabled={topics.length === 0}>
                                Сохранить
                              </button>
                              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                                Отмена
                              </button>
                            </div>
                          </form>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td>{question.id}</td>
                      <td>{getTopicTitle(question.topic_id)}</td>
                      <td>
                        {question.category ? (
                          <span className="category-badge">{question.category}</span>
                        ) : (
                          <span className="category-empty">—</span>
                        )}
                      </td>
                      <td className="question-text">{question.text}</td>
                      <td className="question-text">
                        {question.correct_answer ? (
                          question.correct_answer
                        ) : (
                          <span className="category-empty">—</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-icon btn-regenerate-answer-list"
                          onClick={() => handleRegenerateAnswerInList(question)}
                          disabled={regeneratingAnswerId === question.id}
                          title={
                            regeneratingAnswerId === question.id
                              ? 'Пересоздание...'
                              : 'Пересоздать ответ нейросетью и сохранить в БД'
                          }
                        >
                          {regeneratingAnswerId === question.id ? '⏳' : '🔄'}
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(question)}
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon btn-make-welcome"
                          onClick={() => handleOpenMakeWelcome(question)}
                          title="Сделать Welcome вопросом (перенос в Welcome-вопросы и удаление из списка)"
                        >
                          Welcome
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(question.id)}
                          title="Удалить"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Страница {currentPage} из {totalPages}
          </div>
          <div className="pagination-controls">
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
            >
              ← Назад
            </button>
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`btn pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
            >
              Вперед →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Компонент для статистики вопросов
function QuestionsStatisticsTab() {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/questions/statistics`);
      setStatistics(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке статистики: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Загрузка статистики...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (statistics.length === 0) {
    return (
      <div className="empty-state-container">
        <p>Нет данных для отображения статистики</p>
      </div>
    );
  }

  const categories = ['Junior', 'Middle', 'Senior'];

  return (
    <div className="statistics-tab">
      <div className="statistics-header">
        <h3>Статистика вопросов по темам и категориям</h3>
        <button className="btn btn-secondary" onClick={fetchStatistics}>
          Обновить
        </button>
      </div>

      <div className="statistics-grid">
        {statistics.map((stat) => (
          <div key={stat.topic_id} className="statistics-card">
            <div className="statistics-card-header">
              <h4>{stat.topic_title}</h4>
              <span className="statistics-total">Всего: {stat.total}</span>
            </div>
            
            <div className="statistics-categories">
              {categories.map((category) => {
                const count = stat.categories[category] || 0;
                const percentage = stat.total > 0 ? Math.round((count / stat.total) * 100) : 0;
                
                return (
                  <div key={category} className="statistics-category-item">
                    <div className="category-header">
                      <span className="category-name">{category}</span>
                      <span className="category-count">{count}</span>
                    </div>
                    <div className="category-bar-container">
                      <div 
                        className="category-bar"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="category-percentage">{percentage}%</div>
                  </div>
                );
              })}
              
              {/* Показываем другие категории, если есть */}
              {Object.keys(stat.categories).filter(cat => !categories.includes(cat)).map((category) => {
                const count = stat.categories[category];
                const percentage = stat.total > 0 ? Math.round((count / stat.total) * 100) : 0;
                
                return (
                  <div key={category} className="statistics-category-item">
                    <div className="category-header">
                      <span className="category-name">{category}</span>
                      <span className="category-count">{count}</span>
                    </div>
                    <div className="category-bar-container">
                      <div 
                        className="category-bar"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="category-percentage">{percentage}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default QuestionsTab;
